import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { Permission } from "@/data/mockData";
import { loginApi, logoutApi, getStoredUser, getStoredToken, refreshToken, clearSession, rehydrateSessionFromCookie, type ApiUser, resolveTenant, type TenantResolveResult, rolesApi, initSessionCrypto } from "@/lib/api";

export type AppRole = string;
export type { Permission };
export type { TenantResolveResult };

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "Active" | "Inactive";
  deals: number;
  revenue: number;
  avatar: string;
  phone: string;
}

export const routePermissions: Record<string, Permission> = {
  "/": "view_dashboard",
  "/leads": "view_leads",
  "/deals": "view_deals",
  "/customers": "view_customers",
  "/contacts": "view_contacts",
  "/activities": "view_activities",
  "/reports": "view_reports",
  "/automation": "view_automation",
  "/settings": "view_settings",
  "/access-control": "manage_users",
};

/** Sidebar-aligned order used for post-login / Access Denied redirects. */
export const HOME_ROUTE_CANDIDATES: { path: string; permission: Permission }[] = [
  { path: "/", permission: "view_dashboard" },
  { path: "/leads", permission: "view_leads" },
  { path: "/deals", permission: "view_deals" },
  { path: "/customers", permission: "view_customers" },
  { path: "/contacts", permission: "view_contacts" },
  { path: "/activities", permission: "view_activities" },
  { path: "/reports", permission: "view_reports" },
  { path: "/automation", permission: "view_automation" },
  { path: "/access-control", permission: "manage_users" },
  { path: "/settings", permission: "view_settings" },
];

const PARTNER_LIKE_ROLES = new Set(["Partner", "Dealer"]);

/** Sync permission check against the static fallback map (safe right after login). */
export function hasPermissionForRoles(
  roles: string[],
  permission: Permission,
  type?: string,
): boolean {
  if (type === "superadmin" || roles.includes("SuperAdmin")) return true;
  return roles.some((r) => ROLE_PERMS_FALLBACK[r]?.has(permission) ?? false);
}

/** First route the user is allowed to open. Partners/Dealers prefer Leads. */
export function getHomePath(
  hasPermission: (p: Permission) => boolean,
  roles: string[] = [],
): string {
  if (roles.some((r) => PARTNER_LIKE_ROLES.has(r)) && hasPermission("view_leads")) {
    return "/leads";
  }
  for (const route of HOME_ROUTE_CANDIDATES) {
    if (hasPermission(route.permission)) return route.path;
  }
  return "/";
}

// Hardcoded fallback permissions — used when backend roles haven't loaded yet
// or when the role name matches a known alias.
const ROLE_PERMS_FALLBACK: Record<string, Set<string>> = {
  SuperAdmin: new Set([
    "view_dashboard","view_leads","edit_leads","delete_leads",
    "view_deals","edit_deals","delete_deals",
    "view_customers","edit_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities",
    "view_reports","view_automation","edit_automation",
    "view_settings","manage_users","manage_roles","manage_master_data",
  ]),
  Admin: new Set([
    "view_dashboard","view_leads","edit_leads","delete_leads",
    "view_deals","edit_deals","delete_deals",
    "view_customers","edit_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities",
    "view_reports","view_automation","edit_automation",
    "view_settings","manage_users","manage_roles","manage_master_data",
  ]),
  Manager: new Set([
    "view_dashboard","view_leads","edit_leads",
    "view_deals","edit_deals",
    "view_customers","edit_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities","view_reports","view_automation","view_settings",
  ]),
  // "Sales Manager" alias → same as Manager
  "Sales Manager": new Set([
    "view_dashboard","view_leads","edit_leads",
    "view_deals","edit_deals",
    "view_customers","edit_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities","view_reports","view_automation","view_settings",
  ]),
  Executive: new Set([
    "view_dashboard","view_leads","edit_leads","view_deals","edit_deals",
    "view_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities","view_reports",
  ]),
  "Sales Executive": new Set([
    "view_dashboard","view_leads","edit_leads","view_deals","edit_deals",
    "view_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities","view_reports",
  ]),
  Support: new Set(["view_dashboard","view_customers","view_contacts","view_activities"]),
  // Canonical seeded name is "Support Agent"; "Support" is a legacy alias
  "Support Agent": new Set(["view_dashboard","view_customers","view_contacts","view_activities"]),
  Partner: new Set([
    "view_dashboard","view_leads","edit_leads",
    "view_deals","edit_deals",
    "view_customers","view_contacts","edit_contacts",
    "view_activities","edit_activities","view_reports",
  ]),
  Dealer: new Set([
    "view_dashboard","view_leads","edit_leads",
    "view_deals","view_contacts","edit_contacts",
    "view_activities","edit_activities",
  ]),
};

interface AuthContextType {
  apiUser: ApiUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True while the app is restoring a session from the cookie on page load.
   *  Routes should wait for this before deciding to redirect to /login. */
  isBootstrapping: boolean;
  /**
   * False until /me/permissions has settled for the current user.
   * ProtectedRoute should wait so custom roles aren't denied against an empty fallback.
   */
  permissionsReady: boolean;
  login: (email: string, password: string, tenantCode?: string) => Promise<boolean>;
  resolveTenantCode: (code: string) => Promise<TenantResolveResult>;
  logout: () => void;
  hasPermission: (permission: Permission) => boolean;
  isSuperAdmin: boolean;
  currentUser: CurrentUser | null;
  allUsers: CurrentUser[];
  updateUserRole: (userId: string, newRole: AppRole) => void;
  addUser: (user: any) => void;
  removeUser: (userId: string) => void;
  setUserStatus: (userId: string, status: any) => void;
  myTeam: any | null;
  managedUserIds: string[];
  canSeeLead: (lead: any) => boolean;
  canReassignLead: (lead: any) => boolean;
  canReplyToLead: (lead: any) => boolean;
  reloadRolePermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [apiUser, setApiUser] = useState<ApiUser | null>(() => getStoredUser());
  const [isLoading, setIsLoading] = useState(false);
  const [hasToken, setHasToken] = useState<boolean>(() => !!getStoredToken());
  // When the page first loads without an in-memory token we must try to
  // rehydrate from the httpOnly cookie before any route decides to redirect.
  // Start "bootstrapping" only when there's no token yet (i.e. a fresh load).
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => !getStoredToken());
  // Dynamic role → permissions map loaded from /me/permissions.
  // Falls back to ROLE_PERMS_FALLBACK for known built-in role names.
  const [dynamicRolePerms, setDynamicRolePerms] = useState<Record<string, Set<string>>>({});
  // Gate route checks until the first permissions fetch finishes (success or error).
  const [permissionsReady, setPermissionsReady] = useState<boolean>(false);

  useEffect(() => {
    const handler = () => setApiUser(null);
    window.addEventListener("febix:session-expired", handler);
    return () => window.removeEventListener("febix:session-expired", handler);
  }, []);

  // On boot, the access token only lives in memory and is gone after a full
  // page reload. Try to rehydrate it from the httpOnly `febix_session` cookie.
  useEffect(() => {
    if (getStoredToken()) {
      setApiUser(getStoredUser());
      setHasToken(true);
      setIsBootstrapping(false);
      void initSessionCrypto();
      return;
    }
    void rehydrateSessionFromCookie()
      .then((ok) => {
        if (ok) {
          setApiUser(getStoredUser());
          setHasToken(true);
          void initSessionCrypto();
        } else {
          setApiUser(null);
          setHasToken(false);
        }
      })
      .finally(() => setIsBootstrapping(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadRolePermissions = useCallback(async () => {
    if (!getStoredToken()) {
      setPermissionsReady(true);
      return;
    }
    try {
      // Prefer /me/permissions — available to every authenticated user (incl. Partner/Support).
      // Full /roles list still requires view_settings and is used by Access Control / Settings.
      const me = await rolesApi.myPermissions();
      const perms: Record<string, Set<string>> = {};
      for (const [name, list] of Object.entries(me.byRole ?? {})) {
        perms[name] = new Set(list);
      }
      setDynamicRolePerms(perms);
    } catch {
      // Keep existing or fallback — don't break the app
    } finally {
      setPermissionsReady(true);
    }
  }, []);

  // Load role permissions whenever the user becomes authenticated
  useEffect(() => {
    if (apiUser && getStoredToken()) {
      setPermissionsReady(false);
      void reloadRolePermissions();
    } else if (!apiUser) {
      setDynamicRolePerms({});
      setPermissionsReady(false);
    }
  }, [apiUser, reloadRolePermissions]);

  const login = useCallback(async (email: string, password: string, tenantCode?: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const res = await loginApi(email, password, tenantCode);
      setPermissionsReady(false);
      setApiUser(res.user);
      setHasToken(true);
      // ECDH key-exchange is piggy-backed onto /auth/login. Fall back to the
      // standalone /auth/key-exchange call if the server didn't include the
      // server public key in the login response.
      void initSessionCrypto();
      // Permissions load via the apiUser effect; ProtectedRoute waits on permissionsReady.
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.error("Login failed:", err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resolveTenantCode = useCallback((code: string) => resolveTenant(code), []);

  const logout = useCallback(() => {
    logoutApi();
    setApiUser(null);
    setHasToken(false);
    setDynamicRolePerms({});
    setPermissionsReady(false);
  }, []);

  const isSuperAdmin = apiUser?.type === 'superadmin' || apiUser?.roles?.includes('SuperAdmin') || false;

  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!apiUser) return false;
    if (apiUser.type === 'superadmin' || apiUser.roles?.includes('SuperAdmin')) return true;
    return apiUser.roles?.some(r => {
      // Dynamic (DB /me/permissions) first — required for custom role names
      const dynamicPerms = dynamicRolePerms[r];
      if (dynamicPerms) return dynamicPerms.has(permission);
      // Built-in alias fallback while waiting or if /me failed
      return ROLE_PERMS_FALLBACK[r]?.has(permission) ?? false;
    }) ?? false;
  }, [apiUser, dynamicRolePerms]);

  const currentUser: CurrentUser | null = apiUser ? {
    id: apiUser.sub,
    name: apiUser.email?.split("@")[0] ?? "User",
    email: apiUser.email ?? "",
    role: isSuperAdmin ? "SuperAdmin" : (apiUser.roles?.[0] ?? "Executive"),
    status: "Active" as const,
    deals: 0,
    revenue: 0,
    avatar: "",
    phone: "",
  } : null;

  const allUsers: CurrentUser[] = currentUser ? [currentUser] : [];

  const isManagerOrAbove = useCallback(() => {
    if (!apiUser) return false;
    const managerRoles = ['Admin', 'Manager', 'Sales Manager'];
    return isSuperAdmin || apiUser.roles?.some((r) => managerRoles.includes(r)) || false;
  }, [apiUser, isSuperAdmin]);

  const isAssignedToMe = useCallback((lead: any) => {
    if (!apiUser) return false;
    // Primary: compare Auth0 sub against assignedToAuth0UserId returned by the API
    if (lead.assignedToAuth0UserId && lead.assignedToAuth0UserId === apiUser.sub) return true;
    // Fallback: internal ULID or legacy field
    if (lead.assignedToUserId && lead.assignedToUserId === apiUser.sub) return true;
    if (lead.assignedTo && lead.assignedTo === apiUser.sub) return true;
    // Email fallback for any mismatch
    if (apiUser.email && lead.assignedToEmail && lead.assignedToEmail === apiUser.email) return true;
    return false;
  }, [apiUser]);

  const canSeeLead = useCallback((lead: any) => {
    if (!apiUser) return false;
    if (isManagerOrAbove()) return true;
    return isAssignedToMe(lead);
  }, [apiUser, isManagerOrAbove, isAssignedToMe]);

  const canReassignLead = useCallback((lead: any) => {
    if (!apiUser) return false;
    if (isManagerOrAbove()) return true;
    return isAssignedToMe(lead);
  }, [apiUser, isManagerOrAbove, isAssignedToMe]);

  const canReplyToLead = useCallback((lead: any) => {
    if (!apiUser) return false;
    if (isSuperAdmin || apiUser.roles?.includes("Admin")) return true;
    return isAssignedToMe(lead);
  }, [apiUser, isSuperAdmin, isAssignedToMe]);

  return (
    <AuthContext.Provider value={{
      apiUser,
      isAuthenticated: !!apiUser && hasToken,
      isLoading,
      isBootstrapping,
      permissionsReady,
      login,
      resolveTenantCode,
      logout,
      hasPermission,
      isSuperAdmin,
      currentUser,
      allUsers,
      updateUserRole: () => {},
      addUser: () => {},
      removeUser: () => {},
      setUserStatus: () => {},
      myTeam: null,
      managedUserIds: apiUser ? [apiUser.sub] : [],
      canSeeLead,
      canReassignLead,
      canReplyToLead,
      reloadRolePermissions,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
